import { Breadcrumbs } from "@/components/Breadcrumbs";
import type { ToOptions } from "@tanstack/react-router";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type PageHeaderState = {
  title?: string;
  backLinkOptions?: ToOptions;
  onBackButtonClick?: (e: React.MouseEvent) => void;
  actions?: React.ReactNode;
  breadcrumbs?: React.ComponentProps<typeof Breadcrumbs>;
  hidden: boolean;
};

type PageHeaderContextValue = {
  parentContext: PageHeaderContextValue | null;
  options: Partial<PageHeaderState> | undefined;
  childOptions: Partial<PageHeaderState> | null;
  setChildOptions: Dispatch<SetStateAction<Partial<PageHeaderState> | null>>;
  state: PageHeaderState;
};

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

const mergePageHeaderOptions = (
  ...optionsArray: (Partial<PageHeaderState> | null | undefined)[]
) => {
  const mergedOptions: Partial<PageHeaderState> = {};

  function setValue<K extends keyof PageHeaderState>(
    key: K,
    mergedOptions: Partial<Record<K, PageHeaderState[K]>>,
    options: Partial<Record<K, PageHeaderState[K]>>,
  ) {
    mergedOptions[key] = options[key];
  }

  for (const options of optionsArray) {
    if (!options) continue;

    for (const key of [
      "title",
      "backLinkOptions",
      "actions",
      "breadcrumbs",
      "hidden",
    ] as const) {
      if (options[key] !== undefined) {
        setValue(key, mergedOptions, options);
      }
    }
  }

  return mergedOptions;
};

export function PageHeaderProvider({
  options,
  children,
}: {
  options?: Partial<PageHeaderState>;
  children: React.ReactNode;
}) {
  const parentContext = useContext(PageHeaderContext);

  const [childOptions, setChildOptions] =
    useState<Partial<PageHeaderState> | null>(null);

  const mergedOptions = useMemo(
    () => mergePageHeaderOptions(options, childOptions),
    [options, childOptions],
  );

  const state = useMemo((): PageHeaderState => {
    if (parentContext) return parentContext.state;
    return {
      ...mergedOptions,
      hidden: mergedOptions.hidden ?? false,
    };
  }, [parentContext, mergedOptions]);

  const context = useMemo(
    (): PageHeaderContextValue => ({
      parentContext,
      options,
      childOptions,
      setChildOptions,
      state,
    }),
    [parentContext, options, childOptions, state],
  );

  useEffect(() => {
    if (!parentContext) return;

    parentContext.setChildOptions((prev) => {
      if (prev) throw Error("Branching PageHeaderProviders is not supported");
      return mergedOptions;
    });

    return () => {
      parentContext.setChildOptions((prev) => {
        if (!prev)
          console.error(
            "childOptions were cleaned up twice, this should not happen",
          );
        return null;
      });
    };
  }, [mergedOptions, options, parentContext]);

  return (
    <PageHeaderContext.Provider value={context}>
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageHeader() {
  const context = useContext(PageHeaderContext);
  if (!context)
    throw new Error("usePageHeader must be used within a PageHeaderProvider");

  return useMemo(() => {
    let currentContext = context;
    while (currentContext.parentContext) {
      currentContext = currentContext.parentContext;
    }
    return currentContext.state;
  }, [context]);
}
